import { isPaywallError } from 'hooks/usePaywall/usePaywall';
import { GenericEligibilityCheckResponse } from 'hooks/usePaywall/usePaywall.types';
import { RenewalRequestPopup } from 'services/patientChart/patientChart.types';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Validates a renewal-request refusal popup, whichever response carried it.
 *
 * A `200` and a `4xx` can both carry a `popup`, so both paths must run through this — a half-formed
 * popup must degrade to the generic snackbar rather than render an empty sheet.
 *
 * `title` and `body` are required and must be non-empty: `RenewalRequestPopup` renders them straight into
 * `Common.Modal`, so `{ title: '' }` would produce a blank modal with only `Close`.
 *
 * The `button` is optional and dropped unless **both** `label` and `link` are present and non-empty — a
 * button that cannot act is worse than no button, and the popup is still valid without one. Dropping it
 * never invalidates the popup itself.
 */
export const parseRenewalPopup = (value: unknown): RenewalRequestPopup | null => {
  if (typeof value !== 'object' || value === null) return null;

  const { body, button, title } = value as Partial<RenewalRequestPopup>;
  if (!isNonEmptyString(title) || !isNonEmptyString(body)) return null;

  const hasUsableButton = isNonEmptyString(button?.label) && isNonEmptyString(button?.link);

  return { body, title, ...(hasUsableButton ? { button } : {}) };
};

/**
 * Extracts a backend-composed refusal popup from a failed `POST /prescriptions/renewal/request`.
 *
 * A `4xx` may carry a `popup` just like a `200` does, and it must render rather than falling back to the
 * generic snackbar. Only a response with **no** valid popup (network/connectivity error, or a malformed
 * payload) is a real failure.
 */
export const getRenewalRequestPopup = (error: unknown): RenewalRequestPopup | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;

  return parseRenewalPopup((error as { data?: { data?: { popup?: unknown } } }).data?.data?.popup);
};

/**
 * Returns a 403 eligibility payload only when `handlePaywallError` can actually render it.
 *
 * `isPaywallError` is a status-only guard, so it happily passes a malformed body — and
 * `handlePaywallError` dereferences `action.paywall.code` / `action.link` without checking, throwing a
 * `TypeError` that would escape into an unhandled rejection. Validate the shape here instead.
 *
 * `CHECKOUT` is included deliberately: the old eligibility flow treated it as access-granted, so it must
 * not surface as an error — the handler no-ops for it.
 */
export const getRenewalEligibilityError = (
  error: unknown
): GenericEligibilityCheckResponse | null => {
  if (!isPaywallError<GenericEligibilityCheckResponse>(error)) return null;

  const action = error.data?.data?.action;
  if (!action) return null;

  switch (action.type) {
    case 'OPEN_LINK':
      return typeof action.link === 'string' ? error.data : null;
    case 'SHOW_PAYWALL':
      return typeof action.paywall?.code === 'string' ? error.data : null;
    case 'CHECKOUT':
      return error.data;
    default:
      // An action type the handler would throw on.
      return null;
  }
};
